import React, { useCallback, useEffect, useRef, useState } from 'react';
import { olderMessages, markRead, sendMessage } from '../../api/portal';
import MessageList from './MessageList';
import Composer from './Composer';

/**
 * ConversationPanel — the permanent two-way thread rail (Epic 7 · S7.4, Part A.5).
 *
 * Rail on `lg+` / sheet body on `< lg` (PortalPage owns the responsive shell).
 * Owns the conversation data flow so MessageList / Composer stay presentational:
 *
 *   - On mount (single park): `olderMessages(parkId, now, 30)` → newest 30.
 *   - “Load older” → `olderMessages(parkId, oldestMessage.at, 30)` → prepend;
 *     empty result → `hasMore=false` (stop asking).
 *   - `markRead(parkId)` once when messages arrive and the panel is visible.
 *   - Composer send → `sendMessage({ parkId, itemId: null, body, attachmentPath })`
 *     with an optimistic append (rolls back on error — Part A.8).
 *
 * All-parks view (`parks.length > 1` or `parkId == null`): list of parks
 * (park + latest preview), tapping selects in ParkFilter (`onSelectPark`).
 *
 * Never imports `supabase` directly — `olderMessages`/`markRead`/`sendMessage`
 * are the single data-access surface (Epic 1).
 */
const PAGE_SIZE = 30;

const ConversationPanel = ({ facts, parkId, parks, canWrite, onOpenItem, onSelectPark, focusMessageId, onMessageFocused }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const markedRead = useRef(false);
  const listRef = useRef(null);

  const allParks = Array.isArray(parks) && parks.length > 1;
  const activeParkId = parkId ?? null;

  const loadMessages = useCallback(async (id) => {
    if (id == null) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    markedRead.current = false;
    try {
      const res = await olderMessages(id, new Date().toISOString(), PAGE_SIZE);
      const msgs = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      setMessages(msgs);
      setHasMore(msgs.length === PAGE_SIZE);
    } catch (e) {
      setError(e?.message || 'Couldn’t load conversation.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allParks) { setLoading(false); return; }
    loadMessages(activeParkId);
  }, [activeParkId, allParks, loadMessages]);

  // markRead once when messages arrive (Part A.5). Non-fatal on failure.
  useEffect(() => {
    if (markedRead.current) return;
    if (activeParkId == null || messages.length === 0) return;
    markedRead.current = true;
    markRead({ parkId: activeParkId, what: 'conversation', id: null }).catch(() => {});
  }, [activeParkId, messages.length]);

  const handleLoadOlder = useCallback(async () => {
    if (!hasMore || loadingOlder || messages.length === 0) return;
    const oldest = messages[0];
    const before = oldest?.at || oldest?.when;
    if (!before) { setHasMore(false); return; }
    setLoadingOlder(true);
    try {
      const res = await olderMessages(activeParkId, before, PAGE_SIZE);
      const older = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      if (older.length === 0) { setHasMore(false); }
      else {
        setMessages((prev) => [...older, ...prev]);
        if (older.length < PAGE_SIZE) setHasMore(false);
      }
    } catch (e) {
      setError(e?.message || 'Couldn’t load older messages.');
    } finally {
      setLoadingOlder(false);
    }
    }, [hasMore, loadingOlder, messages, activeParkId]);

  // Optimistic send — append immediately, roll back on error (Part A.8).
  const handleSend = useCallback(async ({ body, attachmentPaths }) => {
    const optimistic = {
      id: `m-opt-${Date.now()}`, from: 'You', is_mine: true, mine: true,
      body, at_label: 'Now', when: 'Now',
    };
    setMessages((prev) => [...prev, optimistic]);
    try {
      await sendMessage({
        parkId: activeParkId, itemId: null, body,
        attachmentPath: attachmentPaths?.[0] ?? null,
      });
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      throw e;
    }
  }, [activeParkId]);

  // Deep-link `message:<id>` (Epic 11 · S11.2, Part E.10): scroll to the message
  // and load older up to 5× if it's not in the 30 already loaded. Fires once per
  // focusMessageId; clears it via onMessageFocused when found (or after 5 tries).
  const focusTries = useRef(0);
  useEffect(() => {
    if (!focusMessageId || loading || allParks) return;
    const found = messages.some((m) => m.id === focusMessageId);
    if (found) {
      const el = listRef.current?.querySelector(`[data-message-id="${CSS.escape(focusMessageId)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onMessageFocused?.();
      focusTries.current = 0;
      return;
    }
    // Not found — load older up to 5× (Part E.10).
    if (focusTries.current >= 5 || !hasMore || loadingOlder) {
      onMessageFocused?.(); // give up + clear so it doesn't loop
      focusTries.current = 0;
      return;
    }
    focusTries.current += 1;
    handleLoadOlder();
  }, [focusMessageId, messages, loading, loadingOlder, hasMore, allParks, handleLoadOlder, onMessageFocused]);

  // ── All-parks view: list of parks, tapping selects in ParkFilter ─────────
  if (allParks) {
    const parkList = Array.isArray(parks) ? parks : [];
    const latest = facts?.latest;
    return (
      <section data-testid="panel-conversation" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Conversation</h3>
        <ul className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="conversation-parks">
          {parkList.map((p) => (
            <li key={p?.client_id ?? p?.id}>
              <button type="button" onClick={() => onSelectPark?.(p?.client_id ?? p?.id)}
                className="w-full text-left py-2 flex items-center justify-between gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg px-2">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p?.name}</span>
                {latest?.preview && <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[50%]">{latest.preview}</span>}
              </button>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  // ── Single-park thread ──────────────────────────────────────────────────────
  const unread = facts?.unread ?? 0;
  return (
    <section data-testid="panel-conversation" className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-4 flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Conversation</h3>
        {unread > 0 && (
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-[#32B1FF] rounded-full">{unread}</span>
        )}
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2" role="alert">{error}</p>}
      <div className="flex-1 overflow-y-auto max-h-[420px]" ref={listRef}>
        {loading ? (
          <div className="space-y-2 py-4" role="status" aria-label="Loading conversation">
            <div className="h-10 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="h-10 w-3/4 rounded bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <span className="sr-only">Loading…</span>
          </div>
        ) : (
          <MessageList messages={messages} hasMore={hasMore} loadingOlder={loadingOlder} onLoadOlder={handleLoadOlder} onOpenItem={onOpenItem} />
        )}
      </div>
      <Composer canWrite={canWrite} parkId={activeParkId} onSend={handleSend} />
    </section>
  );
};

export default ConversationPanel;
